import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import PageHeader from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Copy, DollarSign, Users, TrendingUp, HandCoins, Share2, Loader2,
  ExternalLink, Download, Bell, MailCheck, Smartphone, CheckCircle2,
  AlertCircle, Clock, ChevronRight, LogIn,
} from 'lucide-react';

function StatusBadge({ status }) {
  const map = {
    pending:  { c: 'bg-amber-100 text-amber-800',   l: 'Pending' },
    approved: { c: 'bg-emerald-100 text-emerald-800', l: 'Approved' },
    paid:     { c: 'bg-slate-100 text-slate-700',   l: 'Paid' },
    reversed: { c: 'bg-rose-100 text-rose-800',     l: 'Reversed' },
  };
  const s = map[status] || map.pending;
  return <Badge className={`${s.c} rounded-full hover:${s.c}`}>{s.l}</Badge>;
}

export default function AffiliatePortalPage() {
  const [state, setState] = useState({ loading: true, forbidden: false, aff: null, referrals: [], payouts: [], materials: [] });
  const [savingPrefs, setSavingPrefs] = useState(false);

  const refresh = async () => {
    try {
      const [meR, refR, payR, matR] = await Promise.all([
        api.get('/affiliate/me'),
        api.get('/affiliate/me/referrals'),
        api.get('/affiliate/me/payouts'),
        api.get('/affiliate/me/materials'),
      ]);
      setState({
        loading: false, forbidden: false,
        aff: meR.data.affiliate,
        referrals: refR.data.referrals || [],
        payouts: payR.data.payouts || [],
        materials: matR.data.materials || [],
      });
    } catch (err) {
      const forbidden = err.response?.status === 403 || err.response?.status === 401;
      setState(s => ({ ...s, loading: false, forbidden }));
    }
  };
  useEffect(() => { refresh(); }, []);

  const { aff, referrals, payouts, materials } = state;

  const shareLink = useMemo(() => {
    if (!aff) return '';
    return `${window.location.origin}/?ref=${aff.code}`;
  }, [aff]);

  const copy = (text, label = 'Copied to clipboard') => {
    navigator.clipboard?.writeText(text);
    toast.success(label);
  };

  const savePrefs = async (next) => {
    setSavingPrefs(true);
    try {
      await api.patch('/affiliate/me/preferences', next);
      toast.success('Preferences saved');
      refresh();
    } catch { toast.error('Save failed'); } finally { setSavingPrefs(false); }
  };

  if (state.loading) {
    return <Layout><div className="py-24 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-slate-300" /></div></Layout>;
  }

  if (state.forbidden || !aff) {
    return (
      <Layout>
        <PageHeader eyebrow="Affiliate Portal" title="Not an affiliate yet?" subtitle="You need an approved affiliate account to access this portal." />
        <section className="max-w-2xl mx-auto px-4 py-10 text-center">
          <Card className="rounded-3xl border-[#E6E9EE]"><CardContent className="p-8">
            <HandCoins className="h-12 w-12 mx-auto text-[#C8922F]" />
            <h2 className="font-headline text-2xl text-[#17352D] mt-3">Ready to earn with Royal Palm?</h2>
            <p className="text-slate-600 mt-2">Apply to become an affiliate — earn 8% on services and 15% on shop products for every customer you refer.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <Button asChild className="rounded-xl bg-[#17352D] text-white"><Link to="/affiliate/apply">Apply Now</Link></Button>
              <Button asChild variant="outline" className="rounded-xl"><Link to="/login"><LogIn className="h-4 w-4 mr-1" />Log In</Link></Button>
            </div>
          </CardContent></Card>
        </section>
      </Layout>
    );
  }

  const isPending = aff.status === 'pending';
  const isSuspended = aff.status === 'suspended';

  return (
    <Layout>
      <PageHeader eyebrow="Affiliate Portal" title={`Welcome back, ${aff.name?.split(' ')[0] || 'Rep'}`} subtitle="Track referrals, share your link, and see your commissions in real time." />
      <section className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="affiliate-portal-page">
        {isPending && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900"><strong>Your account is pending approval.</strong> Your code and link are shown below but commissions won&apos;t track until we activate your account. This usually happens within 24-48h.</div>
          </div>
        )}
        {isSuspended && (
          <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-rose-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-rose-900"><strong>Account suspended.</strong> Reach out to support@royalpalmservice.com to reactivate.</div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { l: 'Balance Owed',   v: `$${(aff.balance_owed || 0).toFixed(0)}`,       i: DollarSign, c: 'text-[#C8922F]' },
            { l: 'Pending',        v: `$${(aff.pending_commission || 0).toFixed(0)}`, i: Clock,     c: 'text-amber-700' },
            { l: 'Lifetime Earned',v: `$${(aff.lifetime_earned || 0).toFixed(0)}`,   i: TrendingUp,c: 'text-emerald-700' },
            { l: 'Referrals',      v: aff.lifetime_referrals || 0,                     i: Users,     c: 'text-[#17352D]' },
          ].map(s => (
            <Card key={s.l} className="rounded-2xl border-[#E6E9EE]"><CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{s.l}</div>
                  <div className={`mt-1 font-headline text-2xl ${s.c}`}>{s.v}</div>
                </div>
                <s.i className="h-6 w-6 text-slate-300" />
              </div>
            </CardContent></Card>
          ))}
        </div>

        {/* Share block */}
        <Card className="rounded-3xl border-[#E6E9EE] bg-gradient-to-br from-[#17352D] to-[#1E4338] text-white">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#EED7A6]">Your gear</div>
                <h2 className="font-headline text-2xl mt-1">Code + Share Link</h2>
                <p className="text-sm text-white/70 mt-1 max-w-lg">Give the code out in person. Post the link online. Both earn you the same commission.</p>
              </div>
              <Badge className="bg-[#EED7A6] text-[#17352D] hover:bg-[#EED7A6] rounded-full">Commission: {aff.commission_rate_services}% services · {aff.commission_rate_products}% products</Badge>
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-black/20 border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-wider text-[#EED7A6]">Your unique code</div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 font-mono text-xl truncate" data-testid="portal-code">{aff.code}</div>
                  <Button size="sm" onClick={() => copy(aff.code, 'Code copied')} data-testid="portal-copy-code" className="rounded-lg bg-[#C8922F] hover:bg-[#B27F26] text-white"><Copy className="h-3.5 w-3.5" /></Button>
                </div>
                <p className="text-[11px] text-white/60 mt-2">Say &ldquo;Use code {aff.code} at royalpalmservice.com&rdquo;</p>
              </div>
              <div className="rounded-2xl bg-black/20 border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-wider text-[#EED7A6]">Your share link</div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 font-mono text-xs truncate bg-black/30 px-2 py-1.5 rounded-lg" data-testid="portal-share-link">{shareLink}</div>
                  <Button size="sm" onClick={() => copy(shareLink, 'Link copied')} data-testid="portal-copy-link" className="rounded-lg bg-[#C8922F] hover:bg-[#B27F26] text-white"><Copy className="h-3.5 w-3.5" /></Button>
                </div>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => copy(`Trusted South Florida service — insured. Use my link for a free estimate: ${shareLink}`, 'Post copied')} data-testid="portal-copy-post" className="rounded-lg text-white border-white/30 bg-white/5 hover:bg-white/10"><Share2 className="h-3.5 w-3.5 mr-1" /> Copy Post</Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs: referrals / payouts / materials / prefs */}
        <Tabs defaultValue="referrals">
          <TabsList className="bg-white border border-[#E6E9EE] rounded-xl h-11 p-1">
            <TabsTrigger value="referrals" data-testid="portal-tab-referrals">Referrals</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="portal-tab-payouts">Payouts</TabsTrigger>
            <TabsTrigger value="materials" data-testid="portal-tab-materials">Marketing Kit</TabsTrigger>
            <TabsTrigger value="prefs" data-testid="portal-tab-prefs">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="referrals">
            <Card className="rounded-2xl border-[#E6E9EE]"><CardContent className="p-0">
              {referrals.length === 0 ? (
                <div className="p-10 text-center text-slate-500">
                  <Users className="h-10 w-10 mx-auto text-slate-300" />
                  <div className="mt-3 font-headline text-lg text-[#17352D]">No referrals yet</div>
                  <p className="text-sm">Share your link or code — they&apos;ll appear here the moment someone books or buys.</p>
                </div>
              ) : (
                <div className="divide-y divide-[#E6E9EE]">
                  <div className="px-4 py-2 flex items-center gap-4 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-[#F7F8FA]">
                    <div className="flex-1">Customer / Event</div>
                    <div className="w-24 text-right">Amount</div>
                    <div className="w-24 text-right">Commission</div>
                    <div className="w-24 text-right">Status</div>
                  </div>
                  {referrals.map(r => (
                    <div key={r.id} className="px-4 py-3 flex items-center gap-4 text-sm hover:bg-[#F7F8FA]" data-testid={`portal-ref-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#17352D] truncate">{r.customer_name || r.customer_email || 'Anonymous'}</div>
                        <div className="text-xs text-slate-500">{r.event_type} · {r.target_kind} · {new Date(r.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="w-24 text-right text-slate-700">${(r.target_amount || 0).toFixed(0)}</div>
                      <div className="w-24 text-right font-semibold text-[#C8922F]">${(r.commission_amount || 0).toFixed(2)}</div>
                      <div className="w-24 text-right"><StatusBadge status={r.status} /></div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="payouts">
            <Card className="rounded-2xl border-[#E6E9EE]"><CardContent className="p-0">
              {payouts.length === 0 ? (
                <div className="p-10 text-center text-slate-500">
                  <DollarSign className="h-10 w-10 mx-auto text-slate-300" />
                  <div className="mt-3 font-headline text-lg text-[#17352D]">No payouts yet</div>
                  <p className="text-sm">Payouts are typically sent every Friday via Zelle or CashApp.</p>
                </div>
              ) : (
                <div className="divide-y divide-[#E6E9EE]">
                  {payouts.map(p => (
                    <div key={p.id} className="px-4 py-3 flex items-center gap-4 text-sm hover:bg-[#F7F8FA]">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="font-semibold text-[#17352D]">${p.amount.toFixed(2)} via {p.method}</div>
                        <div className="text-xs text-slate-500">Ref: {p.reference || '—'} · {new Date(p.paid_at).toLocaleDateString()}</div>
                      </div>
                      <Badge className="rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Paid</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="materials">
            <Card className="rounded-2xl border-[#E6E9EE]"><CardContent className="p-5">
              {materials.length === 0 ? (
                <div className="py-10 text-center text-slate-500">
                  <Download className="h-10 w-10 mx-auto text-slate-300" />
                  <div className="mt-3 font-headline text-lg text-[#17352D]">Kit coming soon</div>
                  <p className="text-sm">Flyers, social graphics, and cold-outreach scripts will appear here shortly.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {materials.map(m => (
                    <a key={m.id} href={m.file_url} target="_blank" rel="noreferrer" className="block rounded-xl border border-[#E6E9EE] hover:border-[#C8922F] hover:shadow p-4 transition-colors-only" data-testid={`portal-material-${m.id}`}>
                      <Badge variant="outline" className="rounded-full text-[10px] mb-2 capitalize">{m.category}</Badge>
                      <div className="font-headline text-lg text-[#17352D]">{m.title}</div>
                      {m.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{m.description}</p>}
                      <div className="mt-3 flex items-center text-sm text-[#C8922F] font-semibold"><Download className="h-4 w-4 mr-1" /> Download <ChevronRight className="h-4 w-4 ml-auto" /></div>
                    </a>
                  ))}
                </div>
              )}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="prefs">
            <Card className="rounded-2xl border-[#E6E9EE]"><CardContent className="p-5 space-y-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Notifications</div>
                <div className="mt-3 space-y-3">
                  <label className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-sm text-slate-700"><MailCheck className="h-4 w-4" /> Email me when I earn a commission</span>
                    <Switch checked={!!aff.notify_email} disabled={savingPrefs} onCheckedChange={(v) => savePrefs({ notify_email: v })} data-testid="portal-notify-email" />
                  </label>
                  <label className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-sm text-slate-700"><Smartphone className="h-4 w-4" /> Text me when I earn a commission</span>
                    <Switch checked={!!aff.notify_sms} disabled={savingPrefs} onCheckedChange={(v) => savePrefs({ notify_sms: v })} data-testid="portal-notify-sms" />
                  </label>
                  <label className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-sm text-slate-700"><Bell className="h-4 w-4" /> Show my name on the public leaderboard</span>
                    <Switch checked={aff.public_opt_in !== false} disabled={savingPrefs} onCheckedChange={(v) => savePrefs({ public_opt_in: v })} data-testid="portal-public-opt" />
                  </label>
                </div>
              </div>
              <div className="pt-3 border-t border-[#E6E9EE]">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Contact info</div>
                <div className="mt-3 grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Phone</label>
                    <Input defaultValue={aff.phone || ''} onBlur={(e) => e.target.value !== (aff.phone || '') && savePrefs({ phone: e.target.value })} placeholder="561-555-0123" className="rounded-xl mt-1" data-testid="portal-phone" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">City</label>
                    <Input defaultValue={aff.city || ''} onBlur={(e) => e.target.value !== (aff.city || '') && savePrefs({ city: e.target.value })} placeholder="West Palm Beach, FL" className="rounded-xl mt-1" data-testid="portal-city" />
                  </div>
                </div>
              </div>
              <div className="text-[11px] text-slate-500 pt-2">Need to change your name, email, or rates? Email <a href="mailto:support@royalpalmservice.com" className="underline">support@royalpalmservice.com</a> and we&apos;ll update your record.</div>
            </CardContent></Card>
          </TabsContent>
        </Tabs>

        <div className="text-center pt-4">
          <a href={`/affiliate/leaderboard`} className="text-sm text-[#17352D] hover:text-[#C8922F] underline inline-flex items-center gap-1">See where I rank on the leaderboard <ExternalLink className="h-3 w-3" /></a>
        </div>
      </section>
    </Layout>
  );
}
